// lib/microsite/visitRewardUx.ts
//
// Reglas PURAS del premio de visitas en el carrito del micrositio (PR 5b). Sin
// React: lo que el cliente VE (monto, ofertas, avisos) y lo que viaja a
// `/api/online-orders/session` (`visitRewardId`).
//
// 🔴 NADA de acá decide dinero. `/session` acuña el cupón del premio, recomputa el
// descuento desde precios de la BD (priceOnlineOrder) y rechaza antes de cobrar.
// Esto es la vista previa. La ÚNICA matemática del premio es `visitRewardDiscount`
// (lib/visitRewards/couponDiscount): aquí solo se arman sus líneas desde el carrito.

import { lineIsCouponAlcohol, type CouponAlcoholFacts } from "@/lib/coupons/alcoholExclusion"
import { visitRewardCouponLine, visitRewardDiscount, type VisitRewardDiscountResult } from "@/lib/visitRewards/couponDiscount"
import type { PricingConfig } from "@/lib/utils/pricing"
import { micrositeLineBasePrice, micrositeLineSubtotal, micrositeCartSubtotal, type AppliedCoupon } from "@/lib/microsite/couponUx"
import type { CartItem as MicrositeCartItem } from "@/components/microsite/types"
import type { VisitCard } from "@/lib/visitRewards/types"

/** El premio que el comensal eligió usar en este pedido. */
export type AppliedVisitReward = {
  rewardId: string
  /** `reward_label` del programa («empanada»). */
  label: string
  /** Ítems elegibles del programa (el ámbito del cupón que acuñará /session). */
  eligibleItemIds: string[]
}

/**
 * Monto del premio sobre el carrito: 100 % de UNA unidad elegible, sin sus
 * modificadores (el extra se cobra). Mismas reglas de línea que el servidor
 * (lib/online-orders/pricing): base = precio CARD de una unidad sin modificadores;
 * línea por peso nunca elegible; `alcohol` excluye líneas como el cupón.
 */
export function micrositeVisitRewardDiscount(
  cart: readonly MicrositeCartItem[],
  pricingCfg: PricingConfig,
  reward: { eligibleItemIds: string[] },
  alcohol?: CouponAlcoholFacts,
): VisitRewardDiscountResult {
  const lines = cart.map((i) =>
    visitRewardCouponLine({
      item_id: i.itemId,
      category_id: null,
      base_price: i.weightMilli != null ? 0 : micrositeLineBasePrice(i, pricingCfg),
      line_total: micrositeLineSubtotal(i, pricingCfg),
      quantity: i.quantity,
      weight: i.weightMilli != null ? i.weightMilli / 1000 : null,
      excluded: alcohol
        ? lineIsCouponAlcohol({ item_id: i.itemId, optionIds: i.modifiers.map((m) => m.modifier_option_id) }, alcohol)
        : false,
    }),
  )
  return visitRewardDiscount(
    {
      discount_type: "percent",
      discount_value: 100,
      grant_kind: "visit_reward",
      scope: { item_ids: reward.eligibleItemIds, category_ids: [] },
      max_scope_units: 1,
    },
    lines,
    micrositeCartSubtotal(cart, pricingCfg),
  )
}

// ─── /api/visit-rewards/my-card ──────────────────────────────────────────────

/** Lo que el carrito sabe de la cuenta del comensal en este local. */
export type MyCardResult =
  | { kind: "signed_out" }
  | { kind: "offer"; card: VisitCard; eligibleItemIds: string[]; phoneLast4: string }

export const MY_CARD_TIMEOUT_MS = 800

/**
 * Consulta la tarjeta del comensal con sesión. `null` = no mostrar nada: flag
 * apagado, cuenta sin verificar, error, forma rara o más de `ms` (el checkout no
 * espera a esto). Nunca lanza.
 */
export async function fetchMyCardWithTimeout(
  locationId: string,
  ms = MY_CARD_TIMEOUT_MS,
): Promise<MyCardResult | null> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller?.abort()
      resolve(null)
    }, ms)
  })
  const request = (async (): Promise<MyCardResult | null> => {
    try {
      const res = await fetch(`/api/visit-rewards/my-card?locationId=${encodeURIComponent(locationId)}`, {
        signal: controller?.signal,
      })
      if (!res.ok) return null
      return parseMyCard(await res.json().catch(() => null))
    } catch {
      return null
    }
  })()
  try {
    return await Promise.race([request, timeout])
  } finally {
    clearTimeout(timer)
  }
}

export function parseMyCard(raw: unknown): MyCardResult | null {
  if (!raw || typeof raw !== "object") return null
  const b = raw as Record<string, unknown>
  if (b.enabled === false) return null
  if (b.signedIn === false) return { kind: "signed_out" }
  if (b.signedIn !== true || b.verified !== true) return null
  const c = b.card as Record<string, unknown> | null
  if (!c || typeof c.rewardLabel !== "string" || !Array.isArray(c.rewards)) return null
  const rewards: VisitCard["rewards"] = []
  for (const r of c.rewards) {
    if (!r || typeof r !== "object") return null
    const { id, expiresAt, held } = r as Record<string, unknown>
    if (typeof id !== "string" || typeof expiresAt !== "string") return null
    rewards.push(held === true ? { id, expiresAt, held: true } : { id, expiresAt })
  }
  const eligibleItemIds = Array.isArray(b.eligibleItemIds)
    ? b.eligibleItemIds.filter((x): x is string => typeof x === "string")
    : []
  return {
    kind: "offer",
    card: {
      // Mismo criterio que fetchCardWithTimeout tras #2666: servidor viejo (sin la clave) = programa prendido.
      programEnabled: c.programEnabled !== false,
      visitsRequired: typeof c.visitsRequired === "number" ? c.visitsRequired : 0,
      progress: typeof c.progress === "number" ? c.progress : 0,
      rewardLabel: c.rewardLabel,
      rewards,
    },
    eligibleItemIds,
    phoneLast4: typeof b.phoneLast4 === "string" ? b.phoneLast4 : "",
  }
}

/** Premios que se pueden ofrecer: los que no tienen ya una orden encima (`held`). */
export function availableRewards(card: VisitCard): VisitCard["rewards"] {
  return card.rewards.filter((r) => r.held !== true)
}

// ─── Rechazos de /session ────────────────────────────────────────────────────

export type VisitRewardRejection =
  | { kind: "phone_mismatch"; phoneLast4: string }
  | { kind: "in_use" }
  | { kind: "expired" }
  | { kind: "no_eligible_line" }
  /** Cualquier otro rechazo del premio (sin sesión, otra cuenta, módulo apagado, sin ítems, migración pendiente). */
  | { kind: "unavailable" }

/**
 * ¿La respuesta de `/session` es un rechazo DEL PREMIO? Solo se llama cuando el
 * body llevó `visitRewardId`. Todos son PRE-cobro (nadie pagó): el padre suelta el
 * premio y deja seguir sin él. `null` = otro fallo (carrito, red, North): no es
 * culpa del premio.
 */
export function parseVisitRewardRejection(status: number, body: unknown): VisitRewardRejection | null {
  const data = body as { error?: unknown; reason?: unknown; phoneLast4?: unknown } | null
  if (!data || typeof data.error !== "string") return null
  if (status === 422 && data.error === "coupon_rejected") {
    return data.reason === "visit_reward_no_eligible_line" ? { kind: "no_eligible_line" } : null
  }
  switch (data.error) {
    case "visit_reward_phone_mismatch":
      return status === 422
        ? { kind: "phone_mismatch", phoneLast4: typeof data.phoneLast4 === "string" ? data.phoneLast4 : "" }
        : null
    case "not_available":
      return status === 409 ? { kind: "in_use" } : null
    case "expired":
      return status === 410 ? { kind: "expired" } : null
    case "visit_reward_not_enabled":
    case "visit_reward_auth":
    case "wrong_owner":
    case "no_eligible_items":
    case "unavailable":
      return { kind: "unavailable" }
    default:
      return null
  }
}

/**
 * ¿El rechazo de la ESCRITURA del camino $0 (POST /api/online-orders con
 * `zero_<intent>`) es del premio? Solo el 409 `not_available`: el premio dejó de
 * servir entre /session y la escritura (p. ej. el kiosko lo canjeó un instante
 * antes). Nadie cobró nada ($0): se suelta el premio con el aviso «en uso». Todo
 * otro fallo de la escritura (400 zero_total_mismatch, red…) sigue el camino de
 * siempre. Más estricto que parseVisitRewardRejection a propósito: esta ruta tiene
 * sus propios `error` y no todos son del premio.
 */
export function parseZeroWriteVisitRewardRejection(status: number, body: unknown): VisitRewardRejection | null {
  const error = (body as { error?: unknown } | null)?.error
  return status === 409 && error === "not_available" ? { kind: "in_use" } : null
}

/** Aviso de una línea que el carrito muestra (clave bajo `…checkout.visitReward.*`). */
export type VisitRewardNotice =
  | VisitRewardRejection
  | { kind: "replaced_coupon" }
  | { kind: "replaced_by_coupon" }

// ─── Un pedido lleva UN solo cupón (`orders.coupon_id`) ──────────────────────


export type CouponOrRewardState = { coupon: AppliedCoupon | null; reward: AppliedVisitReward | null; notice: VisitRewardNotice | null }

/** Aplicar el premio quita el cupón aplicado (y avisa en una línea). */
export function selectVisitReward(
  s: Pick<CouponOrRewardState, "coupon">,
  reward: AppliedVisitReward,
): CouponOrRewardState {
  return { coupon: null, reward, notice: s.coupon ? { kind: "replaced_coupon" } : null }
}

/** Aplicar un cupón quita el premio aplicado (y avisa en una línea). */
export function selectCoupon(
  s: Pick<CouponOrRewardState, "reward">,
  coupon: AppliedCoupon,
): CouponOrRewardState {
  return { coupon, reward: null, notice: s.reward ? { kind: "replaced_by_coupon" } : null }
}
